import produce from 'immer';
import stableStringify from 'json-stable-stringify';
import isObject from 'lodash/isObject';
import memoize from 'lodash/memoize';

import {Section, SectionItem, SubLabelTheme} from 'common/components/items-menu/types';
import {getIconForEvent, renameEvent} from 'common/report/util/events';
import {baseComparator} from 'common/util';
import {hasUserFilters, isAutoTrackCustomEvent} from 'common/util/custom_events';
import {EventDefinition, MpEvent} from 'common/util/definitions-types';
import {
  Permission,
  ProjectFeatureFlag,
  SESSION_END_EVENT_NAME,
  SESSION_START_EVENT_NAME,
  SPECIAL_EVENT_VALUES,
  SPECIAL_EVENTS,
} from 'common/report/constants';
import {customEventsStore} from 'common/report/stores/custom-events';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {currentProjectStore} from 'common/report/stores/project-details';
import {Store} from 'common/report/stores/store';
import {getEventsReportUrlForEvent, getProjectId, getWorkspaceId} from 'common/report/util';
import {lexicalCompose} from 'common/util/function';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {getTooltipDataForEvent} from 'common/widgets/context-panel/util';
import {assertUnreachable} from 'common/util/typescript';
import {PresetDateRange} from 'common/widgets/date-range-picker/models/presets';
import {reporter} from 'common/report/error-reporting';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {OrderBy} from 'common/util/array';
import {PATH_SEPARATOR} from 'common/constants';
import {projectHasEventApprovalTurnedOn} from 'common/util/event-approval';

import {ActionType, EventItem, EventMenuItem, StageableMenuItem, StageTagMenuItem} from '../events-menu/types';
import {MenuType} from '../events-menu/types';
import {sortItemsByLabel} from '../utils/util';
import {SortBy} from '../properties-menu/types';
import {BaseDataController} from './base-controller';

const orderByLabel = lexicalCompose(
  baseComparator<{label?: string}, string>({transform: (item) => item.label && item.label.toLowerCase()}),
);

export type EventsControllerSectionConfig = {
  dataGroupId?: string;
  excludeAutotrackEvents?: boolean;
  excludeCustomEvents?: boolean;
  hideEditControls?: boolean;
  includeTags?: boolean;
  searchFilter?: string;
  selected?: EventItem;
  showAllEvents?: boolean;
  showAnyEvent?: boolean;
  includeHiddenSections?: boolean;
  showSessionEvent?: boolean;
  showSpecialDatasetProperty?: boolean;
  showTopEvents?: boolean;
  mainSectionShowLimit?: number;
  stagedItem?: StageableMenuItem;
  sortByValue?: SortBy;
  recentlyUsedEvents?: EventItem[];
  isBetaMenu?: boolean;
  subLabel?: string;
  mainSectionLabel?: string;
  tagFilter?: string;
};
export class EventsController extends BaseDataController {
  protected onStoreCallback = () => {
    this.updateEventItems();
    this.getContextPanelData.cache.clear();
  };
  public data = new Map<string, SectionItem<EventItem>>();
  public loading: boolean;

  get providerStores(): Array<Store<unknown>> {
    return [customEventsStore, eventDefinitionsStore];
  }

  public canWriteCustomEvents(): boolean {
    return Boolean(currentProjectStore.value?.hasPermission(Permission.WriteCustomEvents));
  }

  public fetchData() {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.data = new Map();

    customEventsStore.fetch({projectId: getProjectId(), workspaceId: getWorkspaceId()});

    eventDefinitionsStore.fetch({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
    });

    this.updateComponents();
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public getSections({
    dataGroupId = null,
    excludeAutotrackEvents,
    excludeCustomEvents,
    includeTags = true,
    searchFilter,
    selected,
    showAllEvents,
    showAnyEvent,
    includeHiddenSections = true,
    showSessionEvent,
    showSpecialDatasetProperty,
    showTopEvents,
    mainSectionShowLimit = 0,
    stagedItem,
    sortByValue = null,
    recentlyUsedEvents = [],
    isBetaMenu = false,
    subLabel = null,
    mainSectionLabel = `Events`,
    tagFilter = null,
  }: EventsControllerSectionConfig = {}): Array<Section<EventMenuItem>> {
    let eventItems = this.filterEventItems({
      dataGroupId,
      showSessionEvent: !dataGroupId && !showSpecialDatasetProperty && showSessionEvent,
      excludeAutotrackEvents,
      excludeCustomEvents,
      tagFilter,
    });
    if (isBetaMenu) {
      eventItems = eventItems.map((item) => {
        return {
          ...item,
          subLabel,
          subLabelTheme: subLabel ? SubLabelTheme.Breadcrumb : null,
          icon: getIconForEvent(item.value),
        };
      });
    }

    const hasSelectedValue = selected?.value ?? false;
    if (hasSelectedValue) {
      eventItems = eventItems.map((event) => ({
        ...event,
        isSelected: event.value.value === selected.value,
      }));
    }

    if (stagedItem) {
      switch (stagedItem.menuType) {
        case MenuType.Tags:
          // return the list of tags in the project
          return [this.getEventTagsSection(excludeCustomEvents, isBetaMenu ? null : 2)];
        case MenuType.Tag: {
          return [
            {
              isLoading: this.loading,
              label: isBetaMenu ? null : `Events tagged as ${stagedItem.value}`,
              items: eventItems.filter((item) =>
                eventDefinitionsStore.value[item.value.value]?.tags.includes(stagedItem.value),
              ),
            },
          ];
        }
        default:
          assertUnreachable(stagedItem);
      }
    }

    const createItemFromSpecialEvent = (value, icon): SectionItem<EventItem> => {
      const label = renameEvent(value);
      return {
        icon: isBetaMenu ? `custom-event-beta` : icon,
        label,
        value: {value, label},
        isSelected: value === selected?.value,
        subLabel,
        subLabelTheme: subLabel ? SubLabelTheme.Breadcrumb : null,
      };
    };

    const additionalItems: Array<SectionItem<EventItem>> = [
      showAnyEvent && createItemFromSpecialEvent(SPECIAL_EVENTS.ANY_EVENT, `event`),
      showAllEvents && createItemFromSpecialEvent(SPECIAL_EVENTS.ALL_EVENTS, `star-top-events`),
      /** @todo [jakub] address top events issue, only allow top events if not a rollup project */
      showTopEvents &&
        !showSpecialDatasetProperty &&
        createItemFromSpecialEvent(SPECIAL_EVENTS.TOP_EVENTS, `star-top-events`),
    ].filter(Boolean);

    const sections: Array<Section<EventMenuItem>> = [];

    if (!isBetaMenu && includeTags) {
      sections.push({...this.getEventTagsSection(excludeCustomEvents), hideTopDivider: isBetaMenu});
    }

    sections.push({
      label: isBetaMenu ? null : mainSectionLabel,
      isLoading: this.loading,
      items: this.prepAndSortItems(
        eventItems,
        additionalItems,
        isBetaMenu ? sortByValue : null,
        searchFilter,
        recentlyUsedEvents,
      ),
      showLimit: mainSectionShowLimit,
      hideTopDivider: isBetaMenu,
    });

    if (includeHiddenSections) {
      sections.push({...this.getHiddenEventSection(), hideTopDivider: isBetaMenu});
    }

    return sections;
  }

  prepAndSortItems(
    eventItems: Array<SectionItem<EventItem>>,
    additionalItems: Array<SectionItem<EventItem>>,
    sortByValue: SortBy,
    searchFilter: string,
    recentlyUsedEvents: EventItem[],
  ): Array<SectionItem<EventMenuItem>> {
    if (!sortByValue) {
      // old menu only
      return [...additionalItems, ...eventItems];
    }
    switch (sortByValue) {
      case SortBy.Relevance: {
        return this.sortEventsByScore(
          [...eventItems, ...additionalItems].filter(Boolean),
          recentlyUsedEvents.filter(Boolean),
          searchFilter,
        );
      }
      case SortBy.NameAsc:
        return [...sortItemsByLabel([...additionalItems, ...eventItems].filter(Boolean), {order: OrderBy.Ascending})];
      case SortBy.NameDesc:
        return [...sortItemsByLabel([...additionalItems, ...eventItems].filter(Boolean), {order: OrderBy.Descending})];
      default:
        assertUnreachable(sortByValue);
    }
  }

  getHiddenEventSection(): Section<EventItem> {
    const eventDefinitions = Object.values(eventDefinitionsStore.value);
    const items = eventDefinitions.filter((def) => def.hidden).map(getDataDefinitionsEventMenuItem);

    const eventApprovalInUse = projectHasEventApprovalTurnedOn(currentProjectStore.value);

    return {
      items,
      label: `Hidden Events`,
      isHidden: true,
      tooltip: eventApprovalInUse
        ? {
            text: `New events are hidden by default. \nContact your project admin or owner to change event visibility. \n[Learn More](https://docs.mixpanel.com/docs/admin/data-governance/event-approval)`,
            'max-width': `200px`,
          }
        : null,
      // need an icon when the section gets expanded; this field is ignored when collapsed.
      icon: eventApprovalInUse ? `info` : null,
    };
  }

  getEventTagsSection(excludeCustomEvents = false, showLimit = 2): Section<StageTagMenuItem> {
    const tagCounts: {[tagName: string]: number} = {};
    for (const eventKey of this.data.keys()) {
      eventDefinitionsStore.value[eventKey]?.tags
        .filter(() => !(excludeCustomEvents && this.data.get(eventKey)?.value.custom))
        .forEach((tag) => (tagCounts[tag] = tagCounts[tag] ? tagCounts[tag] + 1 : 1));
    }

    const eventStageTagMenuItems: Array<SectionItem<StageTagMenuItem>> = []; // TODO: our sorting functions breaks type inference
    for (const tagName in tagCounts) {
      const count = tagCounts[tagName];
      eventStageTagMenuItems.push({
        label: `${tagName} (${count})`,
        icon: `tag`,
        hasCaret: true,
        value: {
          action: ActionType.StageMenu,
          menuType: MenuType.Tag,
          value: tagName,
          count,
        },
      });
    }

    return {
      label: `Events tagged as`,
      items: eventStageTagMenuItems.sort(orderByLabel),
      showLimit,
    };
  }

  getCustomEventItems(excludeCustomEvents = false): Array<SectionItem<EventItem>> {
    if (excludeCustomEvents) {
      return [];
    }

    const customEvents = Object.values(customEventsStore.value)
      // entity-sharing : hide custom events that user does not have access to from menus
      .filter((ce) => ce.can_view);

    return customEvents.map((ce) => {
      return getCustomEventMenuItem(ce, eventDefinitionsStore.value[`$custom_event:${ce.id}`]);
    });
  }

  getEventsItems(includeHidden = false): Array<SectionItem<EventItem>> {
    let eventItems: Array<SectionItem<EventItem>> = [];

    // we filter out custom events here to prevent double count. custom events from data definitions store & custom events store are stitched together in getCustomEventItems
    // eventually, we want to abstract the stitching to the definitions store
    eventItems = Object.values(eventDefinitionsStore.value)
      .filter((ed) => !ed.customEventId && (includeHidden ? true : !ed.hidden))
      .map(getDataDefinitionsEventMenuItem);
    return eventItems;
  }

  updateEventItems() {
    let items = [...this.getEventsItems(), ...this.getCustomEventItems()];
    items = items.sort(orderByLabel);

    this.loading = false;
    this.data = new Map(items.map((item) => [item.value.value, item]));
    this.updateComponents();
  }

  /** filters out events based on dropdown configuration */
  filterEventItems({
    showSessionEvent = false,
    excludeAutotrackEvents = false,
    excludeCustomEvents = false,
    dataGroupId = null,
    tagFilter = null,
  } = {}): Array<SectionItem<EventItem>> {
    const customEvents = Object.values(customEventsStore.value);
    const eventItems = Array.from(this.data.values());

    return eventItems.filter(({value: eventItem}) => {
      const matchesTagFilter: boolean =
        tagFilter !== null ? eventItem?.tags && eventItem.tags.includes(tagFilter) : true;

      if (!matchesTagFilter) {
        return false;
      }

      if (eventItem.custom) {
        if (excludeCustomEvents) {
          return false;
        }
        const customEvent = customEventsStore.value[eventItem.id];

        // filters out auto track custom events
        if (excludeAutotrackEvents) {
          return !isAutoTrackCustomEvent(customEvent, customEvents);
          // filters out custom events with user filters when looking at a different data group
        } else if (dataGroupId) {
          return !hasUserFilters(customEvent, customEvents);
        }

        return true;
      }

      // filters out session events from event definitions when session events are not supported
      return [SESSION_START_EVENT_NAME, SESSION_END_EVENT_NAME].includes(eventItem.value)
        ? showSessionEvent && !dataGroupId
        : true;
    });
  }

  public getSampleEventUrl(item: EventItem): string {
    if (!isObject(item)) {
      reporter.warn(`unexpected type for eventItem`, item);
      return ``;
    }
    // Ignore tag and invalid items
    if (item?.value?.type === `tag` || typeof item.value !== `string`) {
      return ``;
    }
    return getEventsReportUrlForEvent({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
      event: item,
      presetDateRange: PresetDateRange.Last30Days,
    });
  }

  public getContextPanelData = memoize(this._getContextPanelData, (item, options) => stableStringify({item, options}));
  public _getContextPanelData(
    item: EventItem,
    recentlyUsedEvents: EventItem[],
    {allowEdit = false, isBetaMenu = false, throughPropertiesMenu = false} = {},
  ): ContextPanelData {
    const canWriteDataDefinition = currentProjectStore.value?.hasPermission(Permission.WriteDataDefinitions);
    const eventDefinitions = Object.values(eventDefinitionsStore.value);

    let tooltipData = null;
    if (item) {
      tooltipData = getTooltipDataForEvent(
        (item as unknown) as MpEvent,
        eventDefinitions,
        this.isEventRecentlyUsed(item, recentlyUsedEvents),
        {
          allowEdit: allowEdit && this.canWriteCustomEvents(),
          allowDescriptionEdit: canWriteDataDefinition,
          sampleEventUrl: this.getSampleEventUrl(item),
        },
      );

      // Move `getTooltipDataForEvent` if simple menu advances out of beta
      if (tooltipData && isBetaMenu) {
        tooltipData = produce(tooltipData, (data) => {
          if (tooltipData?.entityIcon === `custom`) {
            data.entityIcon = `custom-event-beta`;
            data.subHeader = `Custom Event`;
          } else if (SPECIAL_EVENT_VALUES.includes(item.value)) {
            data.entityIcon = `custom-event-beta`;
            data.subHeader = `Custom Event`;
            data.trackedAs = null;
            data.alwaysExpanded = true;
            data.extraInfo = [`Automatically created by Mixpanel`];
          }
        });
      }
    }

    // default tooltipData for entries that do not have definitions
    // We do not want to show context panel on hovering `Select event`
    if (tooltipData === null && item?.label !== `Select event` && !item?.itemFromInput && !throughPropertiesMenu) {
      tooltipData = {
        header: item?.label,
        subHeader: null,
        path: item?.subLabel ? [item.subLabel.split(PATH_SEPARATOR)[0]] : null,
        createdBy: null,
        entityIcon: item?.icon,
        trackedAs: null,
        description: ``,
        editItem: null,
        metrics: [],
        details: [],
        links: [],
        actions: [],
        isRecentlyUsed: item?.isRecentlyUsed,
      };
    }

    return tooltipData;
  }

  private isEventRecentlyUsed(item: EventItem, recentlyUsedEvents: EventItem[]) {
    const recentlyUsedUniqueKeys = new Set(recentlyUsedEvents.map((eventItem) => eventItem.value));
    return recentlyUsedUniqueKeys.has(item.value);
  }

  private sortEventsByScore(
    events: Array<SectionItem<EventItem>>,
    recentlyUsedEvents: EventItem[],
    searchFilter,
  ): Array<SectionItem<EventItem>> {
    const searchScores = {};
    let maxSearchScore = 0;
    let maxPopularity = 0;
    events.forEach((item) => {
      const searchScore = (searchFilter && fuzzyMatch(item.label, searchFilter)?.score) || 0;
      searchScores[item.value.value] = searchScore;
      if (searchScore > maxSearchScore) {
        maxSearchScore = searchScore;
      }

      const popularity = Number(item.uiUniqueQueryCount) || 0;
      if (popularity > maxPopularity) {
        maxPopularity = popularity;
      }
    });

    const SEARCH_SCORE_WEIGHT = 3;
    const RECENTLY_USED_WEIGHT = 2;

    const eventsWithMeta = events.map((sectionItem) => {
      const isRecentlyUsed = this.isEventRecentlyUsed(sectionItem.value, recentlyUsedEvents);
      sectionItem.value.isRecentlyUsed = isRecentlyUsed;
      const recentlyUsed = isRecentlyUsed ? 1 : 0;
      const popularity = maxPopularity ? (Number(sectionItem.uiUniqueQueryCount) || 0) / maxPopularity : 0;
      const searchScore = searchScores[sectionItem.value.value];
      const normalizedSearchScore = maxSearchScore ? searchScore / maxSearchScore : 0;
      const score = normalizedSearchScore * SEARCH_SCORE_WEIGHT + recentlyUsed * RECENTLY_USED_WEIGHT + popularity;

      if (isNaN(score)) {
        throw Error(`Score is NaN.`);
      }

      sectionItem.score = score;
      return {item: sectionItem, recentlyUsed, popularity, score};
    });

    eventsWithMeta.sort((a, b) => {
      return b.score - a.score;
    });

    if (currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.Debug)) {
      eventsWithMeta.forEach((entry) => {
        const meta = {...entry};
        delete meta.item;
        entry.item.subLabel = JSON.stringify(meta);
      });
    }

    return eventsWithMeta.map((entry) => entry.item);
  }
}

/**
 * Prepare one top event entry for display in mp-items-menu
 * @param {object} topEvent - top event from query response
 * @return {object} - object compatible with mp-items-menu component
 */
export function getEventMenuItem(topEvent, eventDefinition?: EventDefinition): SectionItem<EventItem> {
  const label = renameEvent(topEvent.name || topEvent.label || topEvent.value);
  return {
    label,
    icon: getIconForEvent(topEvent),
    searchableContent: eventDefinition?.name,
    uiUniqueQueryCount: eventDefinition?.uiUniqueQueryCount,
    value: {
      label,
      custom: false,
      id: null,
      value: topEvent.name || topEvent.value,
    },
  };
}

/**
 * Prepare one custom event entry for display in mp-items-menu
 * @param {object} customEvent - custom event from query response
 * @return {object} - object compatible with mp-items-menu component
 */
export function getCustomEventMenuItem(customEvent, eventDefinition?: EventDefinition): SectionItem<EventItem> {
  return {
    icon: `custom`,
    label: customEvent.name,
    /** @todo [pablo] `uiUniqueQueryCount` is not an item relevant to mp-items-menu */
    uiUniqueQueryCount: eventDefinition?.uiUniqueQueryCount,
    value: {
      isCollectEverything: customEvent.is_collect_everything_event,
      custom: true,
      id: customEvent.id,
      label: customEvent.name,
      value: `$custom_event:${customEvent.id}`,
      tags: eventDefinition?.tags,
    },
  };
}

function getDataDefinitionsEventMenuItem(eventDefinition: EventDefinition): SectionItem<EventItem> {
  const isCustomEvent = Boolean(eventDefinition.customEventId);
  const eventItem: EventItem = {
    label: isCustomEvent ? eventDefinition.name : renameEvent(eventDefinition.name),
    value: isCustomEvent ? `$custom_event:${eventDefinition.customEventId}` : eventDefinition.name,
    ...(isCustomEvent ? {id: eventDefinition.customEventId, custom: true} : {}),
    tags: eventDefinition.tags,
  };
  return {
    icon: getIconForEvent(eventItem),
    label: eventItem.label,
    searchableContent: eventDefinition?.name,
    /** @todo [pablo] `uiUniqueQueryCount` is not an item relevant to mp-items-menu */
    uiUniqueQueryCount: eventDefinition?.uiUniqueQueryCount,
    value: eventItem,
  };
}
